import { makeAction, makeReducer } from '@finn-no/redux-actions';
import Router from 'next/router';
import type { Dispatch } from 'redux';
import { updateBannerFilterValues } from 'shared/util/bannerParams';
import { debounce } from 'shared/util/debounce';
import { getApiUrl } from '@/api/apiUrl';
import { fetchOffers } from '@/api/fetcher';
import { ApiActionStatus, type OffersResponse, type ThunkResult } from '@/interfaces';
import { filtersToApiQueryParams, filtersToBrowserQueryParams } from '@/lib/utils/queryParamFilterConversions';
import { stringifyParams } from '@/lib/utils/queryString';
import defaultState from './defaultState';

export const actions = {
    setPending: makeAction<OffersResponse>('RESULT_PENDING', (state) => ({
        ...state,
        status: ApiActionStatus.PENDING,
    })),
    setResults: makeAction<OffersResponse>('RESULT_OK', (_, action) => ({
        ...defaultState.results,
        ...action.payload,
        status: ApiActionStatus.OK,
    })),
    setNotModified: makeAction<OffersResponse>('RESULT_NOT_MODIFIED', (state) => ({
        ...state,
        status: ApiActionStatus.OK,
    })),
    setError: makeAction<OffersResponse>('RESULT_ERROR', (state) => ({
        ...state,
        status: ApiActionStatus.ERROR,
    })),
};

export const debouncedFetchOffers = debounce(async (fetch: () => Promise<OffersResponse | null>, dispatch: Dispatch) => {
    try {
        const data = await fetch();
        if (data) {
            dispatch(actions.setResults(data));
        } else {
            dispatch(actions.setNotModified());
        }
    } catch {
        dispatch(actions.setError());
    }
}, 300);

export const getDestinationFromLabel = (label: string | undefined) => {
    if (!label || label.trim() === '') {
        return undefined;
    }
    const parts = label.split(',').map((it) => it.trim());
    return {
        country: parts.pop(),
        city: parts.length ? parts.join(', ') : undefined,
    };
};

export const fetchResults =
    // biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
        (payload?: any): ThunkResult<Promise<void>> =>
        async (dispatch, getState): Promise<void> => {
            dispatch(actions.setPending());
            const queryParams = stringifyParams(filtersToBrowserQueryParams(getState().filters));
            updateBannerFilterValues({
                ...getState().filters,
                destination: getDestinationFromLabel(getState().filters.destinationLabel),
                page: getState().filters.page ? Number.parseInt(getState().filters.page as string, 10) : undefined,
            });
            await Router.replace({ pathname: Router.pathname, query: queryParams }, undefined, { shallow: true });
            debouncedFetchOffers(() => fetchOffers(getApiUrl(), filtersToApiQueryParams({ ...getState().filters, ...payload })), dispatch);
        };

export default actions;
export const resultReducer = makeReducer<OffersResponse>(actions);
